import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { IFilterDto, RolesType, SelectedLanguage, UnitDetailsDto } from 'api/api';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';
import { reactAI } from 'services/telemetry-service';
import { getFilterOptions } from 'state/ducks/filter-search/operations';
import { getUserGroupsChildHierarchy, getUsersSuperAdminGroupsChildHierarchy } from 'state/ducks/groups/operations';
import { getDropDownOptions, getUnitDetails } from 'state/ducks/machine-details/operations';
import { GenericLevelLimits } from 'state/ducks/machine-details/types';
import { deleteMachine, getMachineCount } from 'state/ducks/machines/operations';
import { GetCustomMessages } from 'state/ducks/notification-custom-message/operations';
import { getNotificationDefinitionsForMachine } from 'state/ducks/notifications/operations';
import { Routes } from 'state/ducks/routes';
import { getServiceVideosByMachineTypeOperation } from 'state/ducks/service-video/operations';
import {
	createUnitTracker,
	deleteUnitTracker,
	getUnitTracker,
	updateUnitTrackerId,
} from 'state/ducks/tracker/operations';
import {
	createUnitWeight,
	deleteUnitWeight,
	getUnitWeight,
	updateUnitWeight,
} from 'state/ducks/unit-weight/operations';
import { getUserSettings } from 'state/ducks/user-settings/operations';
import {
	createXtelDeviceTracker,
	deleteXtelDeviceTracker,
	getXtelUnitTracker,
	updateXtelDeviceTrackerId,
} from 'state/ducks/xtel-tracker/operations';
import history from 'state/history';
import { localized } from 'state/i18n';
import { AppState } from 'state/store';
import { getMachineFromIdDataMapOrDefault } from 'utilities/machine-util';
import {
	isAtLeastAdminFromState,
	isAtLeastCountryAdminFromState,
	isAtLeastSuperAdminFromState,
	isServiceTechnicianFromState,
	isServiceToolCodeFromState,
} from 'utilities/roles-util';
import { getEmailFromToken } from 'utilities/token-util';
import { CloseButtonDialog } from 'view/components/video/close-button-dialog';
import VimeoIframe from 'view/components/vimeo-iframe';
import BiTitle from 'view/shared/components/bi-title/bi-title';
import { ConfirmDialog } from '../../../shared/components/dialogs/bi-dialog-confirm/dialog-confirm';
import { Spinner } from '../../spinner/spinner';
import { AddDevice, AddDeviceDto } from './add-device';
import {
	cardDetailsStyles,
	cardGeneralClassNames,
	cardGreenData,
	cardOrangeData,
	cardRedData,
	SubtitleData,
} from './constants';
import CustomMessageDialog from './custom-message-dialog';
import { BleDetailsContent } from './ble-details-content';
import { DefaultDetailsContent } from './default-details-content';
import './details.scss';
import { DialogServiceCodeExistingUnit } from './dialog-service-code-existing-unit';
import {
	getManualItemsFromDeviceDocuments,
	getManualItemsFromServiceTechnicianVideos,
	ManualItem,
	ManualType,
} from './documents-helper';

const mapStateToProps = (state: AppState, ownProps: RouteComponentProps<AppRouterProps>) => {
	const serialNumber = decodeURIComponent(ownProps.match.params.url_id);
	const currMachine = getMachineFromIdDataMapOrDefault(
		state.machineDetailsReducer.machineDetails,
		serialNumber,
		UnitDetailsDto
	);
	const userGroups = state.groupsReducer.groups.find(g => g.id === getEmailFromToken());
	const currentGroup = userGroups?.data.find(g => g.id === currMachine.data.ownerGroupId);
	const userRole = currentGroup?.roleType ?? RolesType.DataOutOfRange;

	const serviceTechnicianVideos = state.serviceVideoReducer.mappedVideos.find(
		vid => vid.name === currMachine.data.unitModelName
	);
	const isLoadingServiceVideos = state.serviceVideoReducer.isLoading;

	const userSetting = state.userSettingReducer.userSettings;
	const userLanguage = userSetting.selectedLanguage;

	const weightIsActive = state.unitWeightReducer.unitWeights.find(
		uw => uw.unitId === currMachine.data.id && uw.isActive
	);

	const xtelTrackerIsActive = state.xtelTrackerReducer.xtelTrackers.find(
		xt => xt.unitId === currMachine.data.id && xt.isActive
	);

	const deviceTrackerIsActive = state.deviceTrackerReducer.unitTrackers.find(
		dt => dt.unitId === currMachine.data.id && dt.isActive
	);

	return {
		appliedFilterDto: state.filterSearchReducer.appliedFilters,
		isAdmin: isAtLeastAdminFromState(state, currentGroup?.id),
		isCountryAdmin: isAtLeastCountryAdminFromState(state, currentGroup?.id),
		isSuperAdmin: isAtLeastSuperAdminFromState(state),
		customMessages: state.notificationCustomMessageReducer.customMessages,
		deviceId: currMachine.data.id,
		signalStrength: currMachine.data?.signalStrength,
		lastOnline: currMachine.data?.lastestChange,
		onlineStatus: currMachine.data.isOnline,
		currMachine: currMachine,
		machineType: currMachine.data?.unitModelName,
		latitude: currMachine.data?.latitude ? currMachine.data?.latitude : currMachine.data?.homeLatitude,
		longitude: currMachine.data?.longitude ? currMachine.data?.longitude : currMachine.data?.homeLongitude,
		deviceTrackerId: state.deviceTrackerReducer.unitTrackers.find(dt => dt.unitId === currMachine.data.id)
			?.trackerId,
		xtelTrackerId: state.xtelTrackerReducer.xtelTrackers.find(dt => dt.unitId === currMachine.data.id)?.trackerId,
		unitWeightId: state.unitWeightReducer.unitWeights.find(dt => dt.unitId === currMachine.data.id)
			?.weighingSerialNumber,
		deviceDocuments: currMachine.data.unitDocument,
		unitController: currMachine.data.controllerName,
		userRole,
		machineDetails: state.machineDetailsReducer.machineDetails,
		serialNumber,
		isServiceTechnician: isServiceTechnicianFromState(state, currentGroup?.id),
		isServiceToolCode: isServiceToolCodeFromState(state, currentGroup?.id),
		serviceTechnicianVideos,
		isLoadingServiceVideos,
		userLanguage,
		weightIsActive,
		xtelTrackerIsActive,
		deviceTrackerIsActive,
	};
};

export const mapDispatchToProps = (dispatch: Dispatch, props: {}) => ({
	getUnitDetails: async (id: string, userLanguage: SelectedLanguage) =>
		(await getUnitDetails(id, userLanguage))(dispatch),
	getUserSettings: async () => (await getUserSettings())(dispatch),
	getDropDownOptions: async () => (await getDropDownOptions())(dispatch),
	getUserGroupsChildHierarchy: async () => (await getUserGroupsChildHierarchy())(dispatch),
	deleteMachine: async (deviceId: number) => await deleteMachine(deviceId)(dispatch),
	getUsersSuperAdminGroupsChildHierarchy: async () => (await getUsersSuperAdminGroupsChildHierarchy())(dispatch),
	getFilterOptions: async () => (await getFilterOptions())(dispatch),
	getMachineCount: async (filter?: IFilterDto) => (await getMachineCount(filter))(dispatch),
	getNotificationCustomMessages: (serialNumber: string) => GetCustomMessages(serialNumber)(dispatch),
	getNotificationDefinitionsForMachine: async (id: string) =>
		(await getNotificationDefinitionsForMachine(id))(dispatch),
	updateDeviceTrackerId: async (deviceId: number, trackerId: string) =>
		await updateUnitTrackerId(deviceId, trackerId)(dispatch),
	getDeviceTracker: async (serialNumber: string) => await getUnitTracker(serialNumber)(dispatch),
	createDeviceTracker: async (deviceId: number, trackerId: string) =>
		await createUnitTracker(deviceId, trackerId)(dispatch),
	deleteDeviceTracker: async (deviceId: number) => await deleteUnitTracker(deviceId)(dispatch),
	getServiceTechnicianVideosByMachineType: async (machineType: string) =>
		(await getServiceVideosByMachineTypeOperation(machineType))(dispatch),
	updateXtelDeviceTrackerId: async (deviceId: number, trackerId: string, message: string) =>
		await updateXtelDeviceTrackerId(deviceId, trackerId, message)(dispatch),
	getXtelUnitTracker: async (unitId: string) => await getXtelUnitTracker(unitId)(dispatch),
	createXtelDeviceTracker: async (deviceId: number, trackerId: string, message: string) =>
		await createXtelDeviceTracker(deviceId, trackerId, message)(dispatch),
	deleteXtelDeviceTracker: async (deviceId: number) => await deleteXtelDeviceTracker(deviceId)(dispatch),
	updateUnitWeight: async (unitId: number, weighingSerialNumber: string) =>
		await updateUnitWeight(unitId, weighingSerialNumber)(dispatch),
	getUnitWeight: async (unitId: string) => await getUnitWeight(unitId)(dispatch),
	createUnitWeight: async (unitId: number, weighingSerialNumber: string) =>
		await createUnitWeight(unitId, weighingSerialNumber)(dispatch),
	deleteUnitWeight: async (unitId: number) => await deleteUnitWeight(unitId)(dispatch),
});

export function getSubtitleDataFromLevel(fillLevel: number): SubtitleData {
	if (fillLevel <= GenericLevelLimits.Low) {
		return cardGreenData;
	}
	if (fillLevel <= GenericLevelLimits.Medium) {
		return cardOrangeData;
	}

	// default
	return cardRedData;
}

interface AppRouterProps {
	url_id: string;
}

type State = {
	deleteDialogVisible: boolean;
	addTrackerDialogVisible: boolean;
	addXtelTrackerDialogVisible: boolean;
	generateServiceCodeDialogVisible: boolean;
	weightDialogVisible: boolean;
	isTrackerDeviceLoading: boolean;
	isXtelTrackerDeviceLoading: boolean;
	isWeightDeviceLoading: boolean;
	messageDialogVisible: boolean;
	currentOpenVideoDialog: string;
	manualItems: ManualItem[];
	addEditHeader: string;
	deviceTrackerId: string;
	xtelDeviceTrackerId: string;
	unitWeightId: string;
	xtelTrackerCustomMessage: string;
	unitController: string;
};

type Props = ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps> &
	RouteComponentProps<AppRouterProps>;

class MachineDetails extends React.PureComponent<Props, State> {
	private containerClassName = `${cardGeneralClassNames.classNameContainer} ${cardDetailsStyles.classNameContainer} card-container-no-padding`;
	constructor(props: Props) {
		super(props);

		// Queue fetch data operation as soon as possible
		this.fetchUnitData(props.serialNumber);
		this.props.getDropDownOptions();
		this.props.getUserGroupsChildHierarchy();
		this.props.getUsersSuperAdminGroupsChildHierarchy();
		this.props.getUserSettings();

		this.state = {
			deleteDialogVisible: false,
			addTrackerDialogVisible: false,
			addXtelTrackerDialogVisible: false,
			generateServiceCodeDialogVisible: false,
			weightDialogVisible: false,
			isTrackerDeviceLoading: false,
			isXtelTrackerDeviceLoading: false,
			isWeightDeviceLoading: false,
			messageDialogVisible: false,
			currentOpenVideoDialog: '',
			manualItems: [],
			addEditHeader: '',
			deviceTrackerId: '',
			xtelDeviceTrackerId: '',
			xtelTrackerCustomMessage: '',
			unitWeightId: '',
			unitController: '',
		};
	}

	// The route only swaps the :url_id param when navigating between two unit details pages, so the
	// component is reused rather than remounted - the unit scoped data has to be refetched explicitly.
	private fetchUnitData(serialNumber: string) {
		this.props.getUnitDetails(serialNumber, this.props.userLanguage);
		this.props.getNotificationDefinitionsForMachine(serialNumber);
		this.props.getDeviceTracker(serialNumber);
		this.props.getXtelUnitTracker(serialNumber);
		this.props.getUnitWeight(serialNumber);
	}

	componentDidMount() {
		if (this.props.isAdmin === true) {
			this.props.getNotificationCustomMessages(this.props.serialNumber);
		}
		this.checkGetServiceVideos();
	}

	componentDidUpdate(prevProps: Props) {
		if (prevProps.serialNumber !== this.props.serialNumber) {
			this.fetchUnitData(this.props.serialNumber);
			if (this.props.isAdmin === true) {
				this.props.getNotificationCustomMessages(this.props.serialNumber);
			}
		}
		if (
			prevProps.deviceDocuments !== this.props.deviceDocuments ||
			prevProps.serviceTechnicianVideos !== this.props.serviceTechnicianVideos
		) {
			this.setState({
				manualItems: getManualItemsFromDeviceDocuments(this.props.deviceDocuments).concat(
					getManualItemsFromServiceTechnicianVideos(this.props.serviceTechnicianVideos?.serviceVideos ?? [])
				),
			});
		}
		if (prevProps.isAdmin !== true && this.props.isAdmin === true) {
			this.props.getNotificationCustomMessages(this.props.serialNumber);
		}
		this.checkGetServiceVideos();
	}

	checkGetServiceVideos = () => {
		if (
			this.props.isServiceTechnician &&
			!this.props.isLoadingServiceVideos &&
			this.props.machineType &&
			!this.props.serviceTechnicianVideos
		) {
			this.props.getServiceTechnicianVideosByMachineType(this.props.machineType);
		}
	};

	public render() {
		const videoDialogs = this.getVideoDialogs();
		return (
			<div>
				<BiTitle title={localized('MachineDetails')}>
					{this.props.currMachine !== undefined && this.props.currMachine.data.isBleTag !== undefined ? (
						this.props.currMachine.data.isBleTag ? (
							<BleDetailsContent
								machineId={this.props.serialNumber}
								urlId={this.props.match.params.url_id}
								currMachine={this.props.currMachine}
								latitude={this.props.latitude}
								longitude={this.props.longitude}
								isAdmin={this.props.isAdmin}
								isCountryAdmin={this.props.isCountryAdmin}
								isSuperAdmin={this.props.isSuperAdmin}
								isServiceTechnician={this.props.isServiceTechnician}
								manualItems={this.state.manualItems}
								onDeletePressed={this.handleDeletePressed}
								onVideoBtnPressed={this.handleVideoBtnPressed}
							/>
						) : (
							<DefaultDetailsContent
								machineId={this.props.serialNumber}
								urlId={this.props.match.params.url_id}
								currMachine={this.props.currMachine}
								onlineStatus={this.props.onlineStatus}
								signalStrength={this.props.signalStrength}
								lastOnline={this.props.lastOnline}
								latitude={this.props.latitude}
								longitude={this.props.longitude}
								isAdmin={this.props.isAdmin}
								isCountryAdmin={this.props.isCountryAdmin}
								isSuperAdmin={this.props.isSuperAdmin}
								isServiceTechnician={this.props.isServiceTechnician}
								isServiceToolCode={this.props.isServiceToolCode}
								userRole={this.props.userRole}
								manualItems={this.state.manualItems}
								deviceTrackerId={this.props.deviceTrackerId}
								deviceTrackerIsActive={!!this.props.deviceTrackerIsActive}
								isTrackerDeviceLoading={this.state.isTrackerDeviceLoading}
								customMessages={this.props.customMessages}
								onDeletePressed={this.handleDeletePressed}
								onServiceToolCodePressed={this.handleServiceToolCodePressed}
								onVideoBtnPressed={this.handleVideoBtnPressed}
								onAddTrackerPressed={this.handleAddTrackerPressed}
								onCustomMessagePressed={this.handleCustomMessagePressed}
							/>
						)
					) : (
						<Spinner />
					)}
				</BiTitle>

				{videoDialogs}
				<ConfirmDialog
					header={localized('DeleteMachine')}
					visible={this.state.deleteDialogVisible}
					onHide={this.handleDeleteCancel}
					confirmColor="org-red"
					confirmMessage={localized('DeleteMachine')}
					dialogMessage={localized('DeleteConfirmMessage')}
					onConfirm={this.handleDeleteConfirmed}
					onCancel={this.handleDeleteCancel}
				/>
				<AddDevice
					header={this.state.addEditHeader}
					visible={this.state.addTrackerDialogVisible}
					isEdit={false}
					onHide={this.handleAddTrackerCancel}
					onConfirm={this.handleAddTrackerConfirmed}
					onDelete={this.handleTrackerDelete}
					currentId={this.props.deviceTrackerId}
					deviceType="t-tracker"
				/>
				<AddDevice
					header={this.state.addEditHeader}
					visible={this.state.addXtelTrackerDialogVisible}
					onHide={this.handleAddXtelTrackerCancel}
					onConfirm={this.handleAddXtelTrackerConfirmed}
					onDelete={this.handleXtelTrackerDelete}
					currentId={this.props.xtelTrackerId}
					currentMessage={this.props.currMachine.data.customXtelMessage}
					deviceType="x-tracker"
				/>
				<AddDevice
					header={this.state.addEditHeader}
					visible={this.state.weightDialogVisible}
					onHide={this.handleWeightExit}
					onConfirm={this.handleWeightUpdated}
					onDelete={this.handleWeightDelete}
					currentId={this.props.unitWeightId}
					deviceType={'weight'}
				/>
				{this.props.isServiceToolCode && (
					<DialogServiceCodeExistingUnit
						visible={this.state.generateServiceCodeDialogVisible}
						onHide={this.handleServiceToolCodeClose}
						machineId={this.props.serialNumber}
					/>
				)}
				<CustomMessageDialog
					visible={this.state.messageDialogVisible}
					onHide={this.handleCustomMessageClose}
					header={localized('CustomMessages')}
					serialNumber={this.props.serialNumber}
				/>
			</div>
		);
	}

	private handleServiceToolCodePressed = () => {
		this.setState({ generateServiceCodeDialogVisible: true });
	};

	private handleServiceToolCodeClose = () => this.setState({ generateServiceCodeDialogVisible: false });

	private handleDeletePressed = () => this.setState({ deleteDialogVisible: true });

	private handleDeleteCancel = () => this.setState({ deleteDialogVisible: false });

	private handleDeleteConfirmed = async () => {
		await this.props.deleteMachine(this.props.deviceId);
		await this.props.getMachineCount(this.props.appliedFilterDto);
		history.push(Routes.Machines);
	};

	private handleAddTrackerPressed = () =>
		this.setState({ addTrackerDialogVisible: true, addEditHeader: localized('Tracker') });

	private handleAddTrackerCancel = () => this.setState({ addTrackerDialogVisible: false });
	private handleAddXtelTrackerCancel = () => this.setState({ addXtelTrackerDialogVisible: false });

	private handleAddTrackerConfirmed = async (dto: AddDeviceDto) => {
		this.setState({ addTrackerDialogVisible: false, isTrackerDeviceLoading: true, deviceTrackerId: dto.id });

		if (this.props.deviceTrackerId === undefined) {
			await this.props.createDeviceTracker(this.props.deviceId, dto.id);
		} else {
			await this.props.updateDeviceTrackerId(this.props.deviceId, dto.id);
		}

		this.setState({ ...this.state, isTrackerDeviceLoading: false });
	};

	private handleAddXtelTrackerConfirmed = async (dto: AddDeviceDto) => {
		this.setState({
			addXtelTrackerDialogVisible: false,
			isXtelTrackerDeviceLoading: true,
			xtelDeviceTrackerId: dto.id,
			xtelTrackerCustomMessage: dto.message,
		});

		if (this.props.xtelTrackerId === undefined) {
			await this.props.createXtelDeviceTracker(this.props.deviceId, dto.id, dto.message);
		} else {
			await this.props.updateXtelDeviceTrackerId(this.props.deviceId, dto.id, dto.message);
		}

		this.setState({ ...this.state, isXtelTrackerDeviceLoading: false });
	};

	private handleTrackerDelete = async () => {
		this.setState({ addTrackerDialogVisible: false, isTrackerDeviceLoading: true });
		await this.props.deleteDeviceTracker(this.props.deviceId);

		this.setState({
			...this.state,
			isTrackerDeviceLoading: false,
			deviceTrackerId: '',
		});
	};

	private handleXtelTrackerDelete = async () => {
		this.setState({ addXtelTrackerDialogVisible: false, isXtelTrackerDeviceLoading: true });
		await this.props.deleteXtelDeviceTracker(this.props.deviceId);

		this.setState({
			...this.state,
			isXtelTrackerDeviceLoading: false,
			xtelDeviceTrackerId: '',
		});
	};

	private handleCustomMessagePressed = () => this.setState({ messageDialogVisible: true });

	private handleCustomMessageClose = () => this.setState({ messageDialogVisible: false });

	private handleWeightUpdated = async (dto: AddDeviceDto) => {
		this.setState({ weightDialogVisible: false, isWeightDeviceLoading: true, unitWeightId: dto.id });

		if (this.props.unitWeightId === undefined) {
			await this.props.createUnitWeight(this.props.deviceId, dto.id);
		} else {
			await this.props.updateUnitWeight(this.props.deviceId, dto.id);
		}

		await this.props.getUnitDetails(this.props.serialNumber, this.props.userLanguage);

		this.setState({ ...this.state, isWeightDeviceLoading: false });
	};

	private handleWeightExit = () => this.setState({ weightDialogVisible: false });

	private handleWeightDelete = async () => {
		this.setState({ weightDialogVisible: false, isWeightDeviceLoading: true });
		await this.props.deleteUnitWeight(this.props.deviceId);

		this.setState({ ...this.state, isWeightDeviceLoading: false, unitWeightId: '' });
	};

	private handleVideoDialogCancel = () => this.setState({ currentOpenVideoDialog: '' });

	private handleVideoBtnPressed = (videoName: string) => this.setState({ currentOpenVideoDialog: videoName });

	private getVideoDialogs = () => {
		return this.state.manualItems
			.filter(manual => manual.type === ManualType.video)
			.map(manual => (
				<CloseButtonDialog
					key={manual.name}
					// manualItem={manual}
					header={manual.name}
					visible={this.state.currentOpenVideoDialog === manual.name}
					onHide={this.handleVideoDialogCancel}
					dismissableMask={true}
				>
					<VimeoIframe videoId={manual.url} />
				</CloseButtonDialog>
			));
	};
}
export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withAITracking(reactAI.reactPlugin, MachineDetails, 'Machine details'));
